import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';

export interface AIUsageEvent {
  task_id: string;
  task_type: string;
  task_name: string;
  credits_used: number;
  cost_usd: number;
  occurred_at: string;
}

export interface AIUsage {
  plan: 'free' | 'pro' | 'enterprise';
  period: string;
  credits_used: number;
  credit_allowance: number;
  credits_remaining: number | null;
  cost_usd: number;
  dollar_allowance: number | null;
  unlimited: boolean;
  enabled: boolean;
  usage_events?: AIUsageEvent[];
}

export function useAIUsage(authenticated: boolean | null): AIUsage | null {
  const [usage, setUsage] = useState<AIUsage | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    if (authenticated !== true) {
      return;
    }
    try {
      const response = await apiFetch('/api/v1/ai/usage');
      if (!response.ok) return;
      setUsage(await response.json<AIUsage>());
    } catch {
      // Usage visibility must never prevent the main workspace from loading.
    }
  }, [authenticated]);

  useEffect(() => {
    void refresh();
    const interval = window.setInterval(() => void refresh(), 60_000);
    const handleFocus = (): void => { void refresh(); };
    window.addEventListener('focus', handleFocus);
    return (): void => {
      window.clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, [refresh]);

  return authenticated === true ? usage : null;
}
